import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { buildParticiplesFromRoot } from './utils/participlesFormI_salim';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import html2canvas from 'html2canvas';
import alarmClockIcon from './imgs/alarm-clock.svg';
import './VerbTenseGame.css';

const FORMS = [
  { id: 'עבר', name: 'עבר' },
  { id: 'הווה-עתיד', name: 'הווה-עתיד' },
  { id: 'ציווי', name: 'ציווי' },
  { id: 'בינוני פועל', name: 'בינוני פועל' },
  { id: 'מצדר', name: 'מצדר' },
  { id: 'בינוני פעול', name: 'בינוני פעול' }
];

const PRONOUNS = [
  { arabic: 'أَنَا' },
  { arabic: 'أَنْتَ' },
  { arabic: 'أَنْتِ' },
  { arabic: 'هُوَ' },
  { arabic: 'هِيَ' },
  { arabic: 'نَحْنُ' },
  { arabic: 'أَنْتُمْ' },
  { arabic: 'أَنْتُنَّ' },
  { arabic: 'هُمْ' },
  { arabic: 'هُنَّ' }
];

const GENDER_OPTIONS = [
  { id: 'masculine', nameHeb: 'יחיד' },
  { id: 'feminine', nameHeb: 'יחידה' },
  { id: 'plural', nameHeb: 'רבים' },
  { id: 'pluralFeminine', nameHeb: 'רבות' }
];

const isAllowed = (allowedValue, genderId) => {
  if (!allowedValue || allowedValue.trim() === '') return false;
  
  const genderMap = {
    'masculine': 'sm',
    'feminine': 'sf',
    'plural': 'pm',
    'pluralFeminine': 'pf'
  };
  
  const allowedStr = allowedValue.toLowerCase().trim();
  if (allowedStr === 'all') return true;
  
  const genderCode = genderMap[genderId];
  if (!genderCode) return false;
  
  const allowedCodes = allowedStr.split(',').map(s => s.trim());
  return allowedCodes.includes(genderCode);
};

function VerbTenseGame3() {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [tenseOrder, setTenseOrder] = useState([]);
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [draggedKey, setDraggedKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState(null);
  const [hoveredLock, setHoveredLock] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [currentAttempts, setCurrentAttempts] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const resultsSummaryRef = useRef(null);
  
  const audioContextRef = useRef(null);
  const usedPoelGenders = useRef([]);
  const usedPaulGenders = useRef([]);
  
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const generateQuestion = useCallback((verbs, tenses, index) => {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }
    
    const verbData = verbs[index];
    const targetTense = tenses[index];
    
    let conjugatedVerb;
    let conjugations;
    
    if (targetTense === 'עבר') {
      conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
      if (conjugations) {
        const availablePronouns = PRONOUNS.filter(p => conjugations[p.arabic]);
        const randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === 'הווה-עתיד') {
      conjugations = conjugatePresentFormI_Salim_fromRow(verbData);
      if (conjugations) {
        const availablePronouns = PRONOUNS.filter(p => conjugations[p.arabic]);
        const randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === 'ציווי') {
      const imperativeForms = buildImperativeFromRow(verbData);
      if (imperativeForms) {
        const imperativeKeys = ['you_ms', 'you_fs', 'you_mp', 'you_fp'];
        const randomKey = imperativeKeys[Math.floor(Math.random() * imperativeKeys.length)];
        conjugatedVerb = imperativeForms[randomKey];
      }
    } else if (targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול') {
      const participles = buildParticiplesFromRoot(verbData);
      if (participles) {
        const benoniPoelAllowed = verbData['benoni_poel_allowed'] || '';
        const benoniPaulAllowed = verbData['benoni_paul_allowed'] || '';
        
        const availableGenders = [];
        GENDER_OPTIONS.forEach(gender => {
          if (targetTense === 'בינוני פועל') {
            if (isAllowed(benoniPoelAllowed, gender.id)) {
              availableGenders.push(gender.id);
            }
          } else {
            if (isAllowed(benoniPaulAllowed, gender.id)) {
              availableGenders.push(gender.id);
            }
          }
        });
        
        if (availableGenders.length > 0) {
          let unusedGenders;
          if (targetTense === 'בינוני פועל') {
            unusedGenders = availableGenders.filter(g => !usedPoelGenders.current.includes(g));
          } else {
            unusedGenders = availableGenders.filter(g => !usedPaulGenders.current.includes(g));
          }
          
          const gendersToChooseFrom = unusedGenders.length > 0 ? unusedGenders : availableGenders;
          const randomGender = gendersToChooseFrom[Math.floor(Math.random() * gendersToChooseFrom.length)];
          
          if (targetTense === 'בינוני פועל') {
            conjugatedVerb = participles.active[randomGender];
            usedPoelGenders.current.push(randomGender);
          } else {
            conjugatedVerb = participles.passive[randomGender];
            usedPaulGenders.current.push(randomGender);
          }
        }
      }
    } else if (targetTense === 'מצדר') {
      const masdar = (verbData['makor1'] || '').trim();
      if (masdar) {
        conjugatedVerb = masdar;
      }
    }
    
    if (!conjugatedVerb) {
      console.error('Failed to generate verb for:', targetTense, verbData);
      return;
    }
    
    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      correctForm: targetTense
    });
    
    setFeedback(null);
    setSelectedLock(null);
    setDraggedKey(false);
    setIsDragging(false);
    setDraggedElement(null);
    setHoveredLock(null);
    setCurrentAttempts(0);
    setHasError(false);
  }, []);

  const createBalancedTenseSequence = () => {
    const allForms = ['עבר', 'הווה-עתיד', 'ציווי', 'בינוני פועל', 'בינוני פעול', 'מצדר'];
    const countAdjacentRepeatedForms = (sequence) =>
      sequence.reduce((count, tense, index) => (
        index > 0 && tense === sequence[index - 1] ? count + 1 : count
      ), 0);
    let attempts = 0;
    const maxAttempts = 100;
    
    while (attempts < maxAttempts) {
      attempts++;
      const result = [...allForms];
      
      const extraSlots = 4;
      const tenseCount = {};
      allForms.forEach(f => tenseCount[f] = 1);
      
      for (let i = 0; i < extraSlots; i++) {
        const available = allForms.filter(f => tenseCount[f] < 2);
        if (available.length === 0) break;
        const chosen = available[Math.floor(Math.random() * available.length)];
        result.push(chosen);
        tenseCount[chosen]++;
      }
      
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
      }
      
      if (result.length === 10 && countAdjacentRepeatedForms(result) <= 1) {
        return result;
      }
    }
    
    const fallback = [...allForms, 'עבר', 'הווה-עתיד', 'ציווי', 'מצדר'];
    for (let attempt = 0; attempt < 100; attempt++) {
      const candidate = [...fallback];
      for (let i = candidate.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidate[i], candidate[j]] = [candidate[j], candidate[i]];
      }

      if (countAdjacentRepeatedForms(candidate) <= 1) {
        return candidate;
      }
    }
    return fallback;
  };

  useEffect(() => {
    const loadVerbs = async () => {
      try {
        usedPoelGenders.current = [];
        usedPaulGenders.current = [];
        
        const rows = await loadVerbsCsv('/verbs-grade10.csv');
        
        const baseVerbs = rows.filter(r => {
          const binyan = (r['בניין'] || '').trim();
          const gizra = (r['gizra'] || '').trim();
          const tense = r['זמן'];
          const hasFaVowelFuture = r['R2_FUTURE'] || r['fa_vowel_future'];
          const hasMasdar = (r['makor1'] || '').trim();
          
          return (binyan === '1' || binyan === 'בניין 1') && 
                 gizra.includes('שלמים') && 
                 tense === 'עבר' &&
                 hasFaVowelFuture &&
                 hasMasdar;
        });
        
        const tenseSequence = createBalancedTenseSequence();
        
        const selectedVerbs = [];
        for (const targetTense of tenseSequence) {
          let availableVerbs = [...baseVerbs];
          
          if (targetTense === 'בינוני פעול') {
            availableVerbs = availableVerbs.filter(v => {
              const benoniPaulAllowed = (v['benoni_paul_allowed'] || '').trim();
              return benoniPaulAllowed !== '';
            });
          } else if (targetTense === 'בינוני פועל') {
            availableVerbs = availableVerbs.filter(v => {
              const benoniPoelAllowed = (v['benoni_poel_allowed'] || '').trim();
              return benoniPoelAllowed !== '';
            });
          }
          
          const usedRoots = selectedVerbs.map(v => v['שורש']);
          const unusedVerbs = availableVerbs.filter(v => !usedRoots.includes(v['שורש']));
          
          const poolToUse = unusedVerbs.length > 0 ? unusedVerbs : availableVerbs;
          const randomVerb = poolToUse[Math.floor(Math.random() * poolToUse.length)];
          selectedVerbs.push(randomVerb);
        }
        
        setGameVerbs(selectedVerbs);
        setTenseOrder(tenseSequence);
        generateQuestion(selectedVerbs, tenseSequence, 0);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
      }
    };
    
    loadVerbs();
  }, [generateQuestion]);

  const handlePointerStart = (e) => {
    if (feedback === 'correct') return;
    
    e.preventDefault();
    setDraggedKey(true);
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDraggedElement(e.currentTarget);
    
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.style.zIndex = '1000';
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !draggedElement) return;
    
    const dx = e.clientX - dragStartPos.x;
    const dy = e.clientY - dragStartPos.y;
    
    draggedElement.style.transform = `translate(${dx}px, ${dy}px)`;
    
    const locks = document.querySelectorAll('.lock');
    let foundHoveredLock = null;
    
    locks.forEach((lock) => {
      const rect = lock.getBoundingClientRect();
      const margin = 30;
      
      if (e.clientX >= rect.left - margin &&
          e.clientX <= rect.right + margin &&
          e.clientY >= rect.top - margin &&
          e.clientY <= rect.bottom + margin) {
        
        const formId = lock.getAttribute('data-form-id');
        if (formId) {
          foundHoveredLock = formId;
        }
      }
    });
    
    setHoveredLock(foundHoveredLock);
  };

  const handlePointerEnd = (e) => {
    if (!isDragging || !draggedElement) return;
    
    draggedElement.style.transform = '';
    draggedElement.style.zIndex = '';
    
    if (hoveredLock) {
      setSelectedLock(hoveredLock);
      checkAnswer(hoveredLock);
    }
    
    setIsDragging(false);
    setDraggedKey(false);
    setDraggedElement(null);
    setHoveredLock(null);
  };

  const checkAnswer = (formId) => {
    const isCorrect = formId === currentQuestion.correctForm;
    
    const updatedAttempts = currentAttempts + 1;
    setCurrentAttempts(updatedAttempts);
    
    setFeedback(isCorrect ? 'correct' : 'wrong');
    
    if (isCorrect) {
      playSuccessSound();
      setTimeout(() => {
        saveToHistory(updatedAttempts);
        nextQuestion();
      }, 600);
    } else {
      setHasError(true);
      playErrorSound();
      setTimeout(() => {
        setFeedback(null);
        setSelectedLock(null);
      }, 800);
    }
  };

  const saveToHistory = (finalAttempts) => {
    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      correctForm: currentQuestion.correctForm,
      userAnswer: selectedLock,
      attempts: finalAttempts,
      hadError: hasError
    }]);
  };

  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, tenseOrder, nextIndex);
  };

  const playSuccessSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 523.25;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const playErrorSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleBackToGames = () => {
    navigate(getBackUrl());
  };

  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = 'תוצאות-מהי-הצורה-הדקדוקית.png';
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'תוצאות מהי הצורה הדקדוקית?',
          text: `מהי הצורה הדקדוקית?\nהשגתי ${gameHistory.filter(h => !h.hadError).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('שגיאה בשיתוף:', err);
        }
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = 'תוצאות-מהי-הצורה-הדקדוקית.png';
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (showResults) {
    const correctCount = gameHistory.filter(h => !h.hadError).length;

    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>מהי הצורה הדקדוקית?</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => (
                <div key={idx} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{item.verb}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">{item.correctForm}</span>
                  </div>
                  <div className={`attempts-info ${item.hadError ? 'multiple-attempts' : ''}`}>
                    {!item.hadError ? (
                      <span>✓ תשובה נכונה</span>
                    ) : (
                      <span>✗ נסיונות: {item.attempts}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="grammatical-form-game loading">טוען משחק...</div>;
  }

  // FORMS: עבר(0), הווה-עתיד(1), ציווי(2), בינוני פועל(3), מצדר(4), בינוני פעול(5)
  const topLocksForms = isMobile 
    ? [FORMS[0], FORMS[1]]             // עבר, הווה-עתיד
    : FORMS.slice(0, 3);               // עבר, הווה-עתיד, ציווי
    
  const middleLocksForms = isMobile
    ? [FORMS[2], FORMS[4]]             // ציווי, מצדר
    : null;
    
  const bottomLocksForms = isMobile
    ? [FORMS[3], FORMS[5]]             // בינוני פועל, בינוני פעול
    : FORMS.slice(3, 6);               // בינוני פועל, מצדר, בינוני פעול

  return (
    <div className="grammatical-form-game mobile-six-lock-layout">
      <div className="game-header">
        <button onClick={handleBackToGames} className="back-to-verbs-button">
         ↩ חזרה למשחקי הפועל
        </button>
        <h1>מהי הצורה הדקדוקית?</h1>
        <p className="game-instructions-animate">גרור את היד לשעון הנכון</p>
      </div>

      <div className="grammatical-game-board">
        <div className="locks-row top-locks">
          {topLocksForms.map((form, index) => (
            <div
              key={form.id}
              data-form-id={form.id}
              data-lock-name={form.name}
              className={`lock lock-position-${index} ${
                selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
              } ${hoveredLock === form.id ? 'hovered' : ''}`}
            >
              <span className="lock-icon" aria-hidden="true">
                <img className="lock-icon-image" src={alarmClockIcon} alt="" />
              </span>
              <div className="lock-label">{form.name}</div>
            </div>
          ))}
        </div>

        <div 
          className={`key-container ${draggedKey ? 'dragging' : ''} ${feedback === 'correct' ? 'correct-pulse' : ''}`}
          onPointerDown={handlePointerStart}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="key">
            <div className="key-icon">👈🏻</div>
            <div className="key-verb">{currentQuestion.verb}</div>
          </div>
          <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
        </div>

        {isMobile && middleLocksForms && (
          <div className="locks-row middle-locks">
            {middleLocksForms.map((form, index) => (
              <div
                key={form.id}
                data-form-id={form.id}
                data-lock-name={form.name}
                className={`lock lock-position-${index + 2} ${
                  selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
                } ${hoveredLock === form.id ? 'hovered' : ''}`}
              >
                <span className="lock-icon" aria-hidden="true">
                  <img className="lock-icon-image" src={alarmClockIcon} alt="" />
                </span>
                <div className="lock-label">{form.name}</div>
              </div>
            ))}
          </div>
        )}

        <div className="locks-row bottom-locks">
          {bottomLocksForms.map((form, index) => (
            <div
              key={form.id}
              data-form-id={form.id}
              data-lock-name={form.name}
              className={`lock lock-position-${index + (isMobile ? 4 : 3)} ${
                selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
              } ${hoveredLock === form.id ? 'hovered' : ''}`}
            >
              <span className="lock-icon" aria-hidden="true">
                <img className="lock-icon-image" src={alarmClockIcon} alt="" />
              </span>
              <div className="lock-label">{form.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VerbTenseGame3;
